<template>
  <div class="ticket-logs-container">
    <div class="logs-header">
      <div class="logs-tabs">
        <button
          v-for="tab in tabs"
          :key="tab.key"
          class="logs-tab"
          :class="{ 'logs-tab--active': selectedTab === tab.key }"
          @click="selectedTab = tab.key"
        >
          {{ tab.label }}
          <span class="logs-tab-count">{{ tab.count }}</span>
        </button>
      </div>
    </div>

    <div v-if="uiFlags.isFetching" class="text-center p-4">
      <spinner size="medium" />
      <span class="block mt-2">{{ $t('TICKET_LOGS.LOADING') }}</span>
    </div>

    <div v-else-if="filteredLogs.length === 0" class="empty-logs text-center p-8">
      <div class="empty-icon">
        <fluent-icon icon="document-outline" size="48" />
      </div>
      <p class="empty-text">{{ $t('TICKET_LOGS.EMPTY') }}</p>
    </div>

    <div v-else class="logs-timeline">
      <div
        v-for="(log, index) in filteredLogs"
        :key="log.id"
        class="timeline-item"
      >
        <!-- Left: Date and Time -->
        <div class="timeline-date">
          {{ formatDateRelative(log.created_at) }}
        </div>

        <!-- Center: Timeline line and icon -->
        <div class="timeline-line-container">
          <div class="timeline-line"></div>
          <div class="timeline-icon" :class="`timeline-icon--${log.event_type}`">
            <i v-if="log.event_type === 'status_changed'" class="fi fi-sr-refresh"></i>
            <i v-else-if="log.event_type === 'updated'" class="fi fi-tr-poll-h"></i>
            <i v-else-if="log.event_type === 'reopened'" class="fi fi-tr-rotate-square"></i>
            <i v-else-if="log.event_type === 'activity_created'" class="fi fi-tr-add"></i>
            <i v-else-if="log.event_type === 'activity_updated'" class="fi fi-tr-file-edit"></i>
            <i v-else-if="log.event_type === 'won'" class="fi fi-ts-check-circle"></i>
            <i v-else-if="log.event_type === 'lost'" class="fi fi-tr-circle-xmark"></i>
            <i v-else-if="log.event_type === 'deleted'" class="fi fi-rr-trash"></i>
            <i v-else-if="log.event_type === 'note_created'" class="fi fi-tr-file-edit"></i>
            <i v-else-if="log.event_type === 'note_deleted'" class="fi fi-tr-delete-document"></i>
            <i v-else-if="log.event_type === 'communication_sent'" class="fi fi-rr-paper-plane"></i>
            <i v-else-if="log.event_type === 'communication_failed'" class="fi fi-rr-ban"></i>
            <i v-else-if="log.event_type === 'communication_scheduled'" class="fi fi-tr-calendar-clock"></i>
            <i v-else-if="log.event_type === 'created'" class="fi fi-ts-badge-check"></i>
            <span v-else>{{ getEventEmoji(log.event_type) }}</span>
          </div>
        </div>

        <!-- Right: Activity Card -->
        <div class="timeline-card" :class="{ 'timeline-card--communication': isCommunicationEvent(log.event_type) }">
          <div v-if="!isCommunicationEvent(log.event_type)" class="card-header">
            <div class="card-title">
              {{ getEventTitle(log) }}
            </div>
            <div class="card-user">
              <thumbnail
                :src="log.user && log.user.avatar_url"
                :username="log.user_name"
                size="32px"
                class="user-thumbnail"
              />
              <span class="user-name">{{ log.user_name }}</span>
            </div>
          </div>

          <div class="card-content">
            <div v-if="log.event_type === 'created'" class="event-details">
              <div class="ticket-info">
                <span class="ticket-name">{{ ticketName }}</span>
              </div>
            </div>

            <div v-else-if="log.event_type === 'won'" class="event-details">
              <!-- Removido badge duplicado, o título já informa que foi marcado como ganho -->
            </div>

            <div v-else-if="log.event_type === 'lost'" class="event-details">
              <div v-if="log.new_values_hash && log.new_values_hash.reason" class="loss-reason">
                <strong>{{ $t('TICKET_LOGS.DESCRIPTIONS.REASON') }}</strong> {{ log.new_values_hash.reason }}
              </div>
              <div v-if="log.new_values_hash && log.new_values_hash.comment" class="loss-reason">
                <strong>{{ $t('TICKET_LOGS.DESCRIPTIONS.ADDITIONAL_COMMENT') }}</strong> {{ log.new_values_hash.comment }}
              </div>
            </div>

            <div v-else-if="log.event_type === 'reopened'" class="event-details">
            </div>

            <div v-else-if="log.event_type === 'deleted'" class="event-details">
            </div>

            <div v-else-if="log.event_type === 'communication_sent' || log.event_type === 'communication_failed' || log.event_type === 'communication_scheduled'" class="comm-card-wrapper">
              <!-- Sent/Scheduled message bubble -->
              <div class="comm-bubble" :class="{ 'comm-bubble--failed': log.event_type === 'communication_failed', 'comm-bubble--scheduled': log.event_type === 'communication_scheduled' }">
                <div class="comm-bubble-header">
                  <span class="comm-bubble-meta">{{ formatDateRelative(log.created_at) }} · {{ log.user_name }}</span>
                  <a
                    v-if="log.conversation"
                    :href="conversationUrl(log.conversation.display_id)"
                    class="comm-id-link"
                    target="_blank"
                  >ID: {{ log.conversation.display_id }}</a>
                  <span v-else-if="log.event_type === 'communication_scheduled'" class="comm-scheduled-badge">
                    {{ $t('TICKET_LOGS.COMMUNICATIONS.SCHEDULED_FOR') }}: {{ log.new_values_hash && formatDateRelative(log.new_values_hash.scheduled_at) }}
                  </span>
                </div>
                <div v-if="log.event_type === 'communication_failed'" class="comm-failed-badge">
                  {{ $t('TICKET_LOGS.COMMUNICATIONS.FAILED_REASON') }}: {{ log.new_values_hash && log.new_values_hash.error }}
                </div>
                <div class="comm-bubble-body">{{ log.new_values_hash && log.new_values_hash.body }}</div>
              </div>
              <!-- Reply from contact -->
              <div v-if="log.conversation && log.conversation.last_reply" class="comm-reply-section">
                <div class="comm-reply-from">
                  <span class="comm-reply-arrow">↩</span>
                  <span class="comm-reply-sender">{{ log.conversation.contact_name || $t('TICKET_LOGS.COMMUNICATIONS.CONTACT') }}</span>
                  <span class="comm-reply-date">{{ formatDateRelative(log.conversation.last_reply.created_at) }}</span>
                </div>
                <div class="comm-reply-body">{{ log.conversation.last_reply.content }}</div>
              </div>
              <div v-else-if="log.event_type === 'communication_sent'" class="comm-no-reply">
                {{ $t('TICKET_LOGS.COMMUNICATIONS.NO_REPLY') }}
              </div>
            </div>

            <div v-else-if="log.event_type === 'status_changed'" class="event-details">
              <div class="field-change">
                <strong>{{ $t('TICKET_LOGS.FIELDS.STATUS') }}</strong> {{ $t('TICKET_LOGS.DESCRIPTIONS.CHANGED_TO') }} <strong>{{ log.new_status_value || 'N/A' }}</strong>
              </div>
            </div>

            <div v-else-if="hasChanges(log)" class="event-details">
              <div
                v-for="(value, key) in log.new_values_hash"
                :key="key"
                class="field-change"
              >
                <strong>{{ getFieldLabel(key) }}</strong> <span v-html="getChangeDescription(log.old_values_hash[key], value, key)"></span>
              </div>
            </div>

            <div v-else class="event-details">
              <div class="event-description">{{ log.display_description }}</div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div v-if="hasMoreLogs" class="load-more-container">
      <button
        class="button load-more-button"
        :disabled="uiFlags.isFetchingMore"
        @click="loadMoreLogs"
      >
        <spinner v-if="uiFlags.isFetchingMore" size="small" />
        <span v-else>{{ $t('TICKET_LOGS.LOAD_MORE') }}</span>
      </button>
    </div>
  </div>
</template>

<script>
import timeMixin from '../mixins/time';
import Thumbnail from 'dashboard/components/widgets/Thumbnail';
import Spinner from 'shared/components/Spinner';
import FluentIcon from 'shared/components/FluentIcon/Index';
import TicketLogsAPI from 'dashboard/api/ticketLogs';
import { BUS_EVENTS } from 'shared/constants/busEvents';

export default {
  components: {
    Thumbnail,
    Spinner,
    FluentIcon,
  },
  mixins: [timeMixin],
  props: {
    ticketId: {
      type: [String, Number],
      required: true,
    },
    ticketName: {
      type: String,
      default: '',
    },
    contactId: {
      type: [String, Number],
      default: null,
    },
  },
  data() {
    return {
      selectedTab: 'all',
      page: 1,
      perPage: 20,
      logsRecords: [],
      logsMeta: {
        total: 0,
      },
      loadingState: {
        isFetching: false,
        isFetchingMore: false,
      },
      ticketContactIds: [],
    };
  },
  computed: {
    uiFlags() {
      return this.loadingState;
    },
    logs() {
      return this.logsRecords;
    },
    meta() {
      return this.logsMeta;
    },

    TAB_GROUPS() {
      return {
        activities: ['activity_created', 'activity_updated', 'activity_deleted'],
        notes: ['note_created', 'note_updated', 'note_deleted'],
        changes: [
          'status_changed', 'updated', 'created', 'deleted',
          'won', 'lost', 'reopened', 'priority_changed',
          'description_updated', 'assigned', 'unassigned',
        ],
        communications: ['communication_sent', 'communication_failed', 'communication_scheduled'],
      };
    },

    tabs() {
      const g = this.TAB_GROUPS;
      const countFor = events => (this.logs || []).filter(l => events.includes(l.event_type)).length;
      return [
        { key: 'all',            label: this.$t('TICKET_LOGS.TABS.ALL'),            count: (this.logs || []).length },
        { key: 'activities',    label: this.$t('TICKET_LOGS.TABS.ACTIVITIES'),    count: countFor(g.activities) },
        { key: 'notes',         label: this.$t('TICKET_LOGS.TABS.NOTES'),         count: countFor(g.notes) },
        { key: 'changes',       label: this.$t('TICKET_LOGS.TABS.CHANGES'),       count: countFor(g.changes) },
        { key: 'communications', label: this.$t('TICKET_LOGS.TABS.COMMUNICATIONS'), count: countFor(g.communications) },
      ];
    },

    filteredLogs() {
      const events = this.TAB_GROUPS[this.selectedTab];
      if (!events) return this.logs || [];
      return (this.logs || []).filter(log => events.includes(log.event_type));
    },

    hasMoreLogs() {
      return this.meta.total > this.logs.length;
    },
  },
  mounted() {
    this.resetLogsState();
    this.fetchLogs();
    this.fetchTicketContactIds();
    window.bus.$on(BUS_EVENTS.INCOMING_MESSAGE_RECEIVED, this.onIncomingMessage);
  },
  beforeDestroy() {
    window.bus.$off(BUS_EVENTS.INCOMING_MESSAGE_RECEIVED, this.onIncomingMessage);
  },
  methods: {
    resetLogsState() {
      this.page = 1;
      this.logsRecords = [];
      this.logsMeta = { total: 0 };
      this.loadingState = {
        isFetching: false,
        isFetchingMore: false,
      };
    },
    onIncomingMessage({ contactId }) {
      if (!contactId) return;
      // Verifica se o remetente é algum dos contatos do ticket (pode ser mais de um)
      // eslint-disable-next-line eqeqeq
      const belongs = this.ticketContactIds.some(id => id == contactId);
      if (!belongs) return;

      this.page = 1;
      this.fetchLogs();
    },
    async fetchTicketContactIds() {
      if (!this.ticketId) return;
      try {
        const response = await this.$store.dispatch('getTicketContacts', {
          ticketId: this.ticketId,
        });
        const contacts = Array.isArray(response)
          ? response
          : response?.payload || response?.data || [];
        this.ticketContactIds = contacts.map(c => c.id).filter(Boolean);
      } catch (_e) {
        if (this.contactId) this.ticketContactIds = [this.contactId];
      }
    },
    async fetchLogs() {
      if (!this.ticketId) return;

      try {
        this.loadingState = {
          ...this.loadingState,
          isFetching: this.page === 1,
          isFetchingMore: this.page > 1,
        };

        const response = await TicketLogsAPI.getLogs({
          ticketId: this.ticketId,
          page: this.page,
          perPage: this.perPage,
        });

        const { ticket_logs = [], meta = { total: 0 } } = response.data || {};

        if (this.page === 1) {
          this.logsRecords = ticket_logs;
        } else {
          this.logsRecords = [...this.logsRecords, ...ticket_logs];
        }

        this.logsMeta = meta;
      } catch (error) {
        console.error('Error fetching ticket logs:', error);
      } finally {
        this.loadingState = {
          ...this.loadingState,
          isFetching: false,
          isFetchingMore: false,
        };
      }
    },

    async loadMoreLogs() {
      this.page += 1;
      await this.fetchLogs();
    },

    getEventTypeLabel(eventType) {
      const labels = {
        created: this.$t('TICKET_LOGS.EVENTS.CREATED'),
        updated: this.$t('TICKET_LOGS.EVENTS.UPDATED'),
        status_changed: this.$t('TICKET_LOGS.EVENTS.STATUS_CHANGED'),
        assigned: this.$t('TICKET_LOGS.EVENTS.ASSIGNED'),
        unassigned: this.$t('TICKET_LOGS.EVENTS.UNASSIGNED'),
        priority_changed: this.$t('TICKET_LOGS.EVENTS.PRIORITY_CHANGED'),
        description_updated: this.$t('TICKET_LOGS.EVENTS.DESCRIPTION_UPDATED'),
        deleted: this.$t('TICKET_LOGS.EVENTS.DELETED'),
        won: this.$t('TICKET_LOGS.EVENTS.WON'),
        lost: this.$t('TICKET_LOGS.EVENTS.LOST'),
        reopened: this.$t('TICKET_LOGS.EVENTS.REOPENED'),
        note_created: this.$t('TICKET_LOGS.EVENTS.NOTE_CREATED'),
        note_updated: this.$t('TICKET_LOGS.EVENTS.NOTE_UPDATED'),
        note_deleted: this.$t('TICKET_LOGS.EVENTS.NOTE_DELETED'),
        activity_created: this.$t('TICKET_LOGS.EVENTS.ACTIVITY_CREATED'),
        activity_updated: this.$t('TICKET_LOGS.EVENTS.ACTIVITY_UPDATED'),
        activity_deleted: this.$t('TICKET_LOGS.EVENTS.ACTIVITY_DELETED'),
      };
      return labels[eventType] || eventType;
    },

    getEventIcon(eventType) {
      const icons = {
        created: 'video-add-outline',
        updated: 'document-outline',
        status_changed: 'arrow-clockwise-outline',
        assigned: 'video-add-outline',
        unassigned: 'dismiss-outline',
        priority_changed: 'more-vertical-outline',
        description_updated: 'document-outline',
        deleted: 'dismiss-outline',
        note_created: 'document-add-outline',
        note_updated: 'document-outline',
        note_deleted: 'document-dismiss-outline',
        activity_created: 'calendar-add-outline',
        activity_updated: 'calendar-outline',
        activity_deleted: 'calendar-cancel-outline',
      };
      return icons[eventType] || 'document-outline';
    },

    getEventEmoji(eventType) {
      const emojis = {
        created: '➕',
        updated: '📝',
        status_changed: '🔄',
        assigned: '👤',
        unassigned: '👤',
        priority_changed: '⚡',
        description_updated: '📝',
        deleted: '🗑',
        won: '✅',
        lost: '❌',
        reopened: '🔁',
        note_created: '📌',
        note_updated: '📝',
        note_deleted: '🗑',
        activity_created: '📅',
        activity_updated: '📝',
        activity_deleted: '🗑',
      };
      return emojis[eventType] || '📝';
    },

    getEventTitle(log) {
      const isBusinessOpportunity = log.kanbanfunnel_type_opportunity === 'business' || log.kanbanfunnel_type_opportunity === 'both';
      const entityType = isBusinessOpportunity ? 'Negócio' : 'Ticket';

      const titles = {
        created: `${entityType} criado`,
        updated: `${entityType} atualizado`,
        status_changed: 'Status alterado',
        assigned: `${entityType} atribuído`,
        unassigned: `${entityType} desatribuído`,
        priority_changed: 'Prioridade alterada',
        description_updated: 'Descrição atualizada',
        deleted: `${entityType} excluído`,
        won: 'Negócio marcado como ganho',
        lost: 'Negócio marcado como perda',
        reopened: 'Negócio reaberto',
        note_created: 'Nota adicionada',
        note_updated: 'Nota atualizada',
        note_deleted: 'Nota removida',
        activity_created: 'Atividade criada',
        activity_updated: 'Atividade atualizada',
        activity_deleted: 'Atividade removida',
        communication_sent: this.$t('TICKET_LOGS.EVENTS.COMMUNICATION_SENT'),
        communication_failed: this.$t('TICKET_LOGS.EVENTS.COMMUNICATION_FAILED'),
        communication_scheduled: this.$t('TICKET_LOGS.EVENTS.COMMUNICATION_SCHEDULED'),
      };
      return titles[log.event_type] || this.getEventTypeLabel(log.event_type);
    },

    conversationUrl(displayId) {
      const accountId = this.$route?.params?.accountId || window.chatwootConfig?.accountId;
      return `/app/accounts/${accountId}/conversations/${displayId}`;
    },

    isCommunicationEvent(eventType) {
      return ['communication_sent', 'communication_failed', 'communication_scheduled'].includes(eventType);
    },

    formatDateRelative(date) {
      const d = new Date(date);
      const today = new Date();
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);

      const isToday = d.toDateString() === today.toDateString();
      const isYesterday = d.toDateString() === yesterday.toDateString();

      const locale = this.$root.$i18n.locale || 'pt-BR';
      const time = d.toLocaleTimeString(locale === 'pt_BR' ? 'pt-BR' : locale === 'es' ? 'es-ES' : 'en-US', { hour: '2-digit', minute: '2-digit' });

      if (isToday) {
        return locale === 'es' ? `Hoy a las ${time}` : locale === 'en' ? `Today at ${time}` : `Hoje às ${time}`;
      } else if (isYesterday) {
        return locale === 'es' ? `Ayer a las ${time}` : locale === 'en' ? `Yesterday at ${time}` : `Ontem às ${time}`;
      } else {
        const dateStr = d.toLocaleDateString(locale === 'pt_BR' ? 'pt-BR' : locale === 'es' ? 'es-ES' : 'en-US');
        const atWord = locale === 'es' ? 'a las' : locale === 'en' ? 'at' : 'às';
        return `${dateStr} ${atWord} ${time}`;
      }
    },

    getChangeDescription(oldValue, newValue, field) {
      // For UUID fields, just say it was changed without showing the IDs
      if (this.isUUID(oldValue) || this.isUUID(newValue)) {
        return this.$t('TICKET_LOGS.DESCRIPTIONS.CHANGED');
      }

      // Use descriptive format: just show the new value
      return `${this.$t('TICKET_LOGS.DESCRIPTIONS.CHANGED_TO')} <strong>${this.formatValue(newValue)}</strong>`;
    },

    getEntityType(log) {
      const isBusinessOpportunity = log.kanbanfunnel_type_opportunity === 'business' || log.kanbanfunnel_type_opportunity === 'both';
      return isBusinessOpportunity ? this.$t('TICKET.BUSINESS_TITLE') : this.$t('TICKET.TITLE');
    },

    isUUID(value) {
      if (typeof value !== 'string') return false;
      const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
      return uuidRegex.test(value);
    },

    getFieldLabel(field) {
      const labels = {
        assignment: this.$t('TICKET_LOGS.FIELDS.ASSIGNMENT'),
        priority: this.$t('TICKET_LOGS.FIELDS.PRIORITY'),
        sale_value: this.$t('TICKET_LOGS.FIELDS.SALE_VALUE'),
        text: this.$t('TICKET_LOGS.FIELDS.TEXT'),
        description: this.$t('TICKET_LOGS.FIELDS.DESCRIPTION'),
        labels_ids: this.$t('TICKET_LOGS.FIELDS.LABELS'),
        kanbanfunnel_name: this.$t('TICKET_LOGS.FIELDS.KANBANFUNNEL'),
        type_activity: this.$t('TICKET_LOGS.FIELDS.TYPE_ACTIVITY'),
        subject: this.$t('TICKET_LOGS.FIELDS.SUBJECT'),
        scheduled_at: this.$t('TICKET_LOGS.FIELDS.SCHEDULED_AT'),
        status: this.$t('TICKET_LOGS.FIELDS.STATUS'),
        assignee: this.$t('TICKET_LOGS.FIELDS.ASSIGNEE'),
        columns_id: this.$t('TICKET.FORM.COLUMN.LABEL').replace('*', ''),
        column_id: this.$t('TICKET.FORM.COLUMN.LABEL').replace('*', ''),
      };
      return labels[field] || field;
    },

    hasChanges(log) {
      const hasOldValues = Object.keys(log.old_values_hash || {}).length > 0;
      const hasNewValues = Object.keys(log.new_values_hash || {}).length > 0;

      // Só mostra alterações se houver valores antigos E novos (não conta status pois já aparece na descrição)
      return hasOldValues && hasNewValues;
    },

    isRecent(createdAt) {
      const logTime = new Date(createdAt);
      const fiveMinutesAgo = new Date(Date.now() - 5 * 60 * 1000);
      return logTime > fiveMinutesAgo;
    },

    formatDate(date) {
      const d = new Date(date);
      return d.toLocaleDateString('pt-BR');
    },

    formatTime(date) {
      const d = new Date(date);
      return d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
    },

    formatValue(value) {
      if (value === null || value === undefined || value === '') {
        return '-';
      }
      if (typeof value === 'object') {
        return JSON.stringify(value);
      }
      return value;
    },
  },
  watch: {
    ticketId(newId, oldId) {
      if (!newId || newId === oldId) return;

      this.selectedTab = 'all';
      this.resetLogsState();
      this.fetchLogs();
    },
  },
};
</script>

<style lang="scss" scoped>
.ticket-logs-container {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.logs-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: var(--space-normal);
  padding-bottom: var(--space-smaller);
  border-bottom: 1px solid var(--color-border);
}

.logs-title-section {
  flex: 1;
}

.logs-title {
  font-size: var(--font-size-large);
  font-weight: var(--font-weight-medium);
  margin: 0 0 var(--space-micro) 0;
  color: var(--color-body);
}

.ticket-name {
  font-size: var(--font-size-small);
  color: var(--color-body-light);
  margin: 0;
  font-weight: var(--font-weight-normal);
}

.logs-tabs {
  display: flex;
  gap: 0;
  border-bottom: 2px solid var(--color-border);
  width: 100%;
}

.logs-tab {
  display: inline-flex;
  align-items: center;
  gap: var(--space-smaller);
  padding: var(--space-small) var(--space-normal);
  font-size: var(--font-size-small);
  font-weight: var(--font-weight-medium);
  color: var(--color-body-light);
  background: transparent;
  border: none;
  border-bottom: 2px solid transparent;
  margin-bottom: -2px;
  cursor: pointer;
  white-space: nowrap;
  transition: color 0.15s ease, border-color 0.15s ease;

  &:hover {
    color: var(--color-body);
  }

  &--active {
    color: var(--color-woot);
    border-bottom-color: var(--color-woot);
  }
}

.logs-tab-count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  height: 18px;
  padding: 0 var(--space-micro);
  border-radius: 9px;
  background: var(--color-background-dark);
  color: var(--color-body);
  font-size: var(--font-size-micro);
  font-weight: var(--font-weight-bold);

  .logs-tab--active & {
    background: var(--color-woot-light);
    color: var(--color-woot);
  }
}

.empty-logs {
  color: var(--color-body-light);

  .empty-icon {
    margin-bottom: var(--space-normal);
    color: var(--color-body-lighter);
  }

  .empty-text {
    font-size: var(--font-size-medium);
  }
}

.logs-timeline {
  flex: 1;
  overflow-y: auto;
  padding: var(--space-normal) 0;
}

.timeline-item {
  display: grid;
  grid-template-columns: 150px 60px 1fr;
  gap: var(--space-normal);
  margin-bottom: var(--space-large);
  position: relative;

  &:last-child {
    .timeline-line {
      display: none;
    }
  }
}

.timeline-date {
  text-align: right;
  padding-right: var(--space-normal);
  font-size: var(--font-size-small);
  color: var(--color-body);
  font-weight: var(--font-weight-medium);
  line-height: 1.4;
  padding-top: var(--space-micro);
}

.timeline-line-container {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.timeline-line {
  position: absolute;
  top: 40px;
  bottom: -32px;
  width: 2px;
  background: var(--color-border);
}

.timeline-icon {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: var(--color-surface);
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  z-index: 1;
  flex-shrink: 0;

  i[class^="fi"],
  i[class*=" fi"] {
    font-size: 18px;
    line-height: 1;
    border: 0 !important;
    outline: 0 !important;
    box-shadow: none !important;
    background: transparent !important;
    color: inherit;
  }

  i[class^="fi"]::before,
  i[class*=" fi"]::before,
  i[class^="fi"]::after,
  i[class*=" fi"]::after {
    border: 0 !important;
    outline: 0 !important;
    box-shadow: none !important;
    background: transparent !important;
  }

  &--created {
    background: var(--color-success-light);
  }

  &--status_changed {
    background: var(--color-info-light);
  }

  &--won {
    background: var(--color-success-light);
  }

  &--lost {
    background: var(--color-danger-light);
  }

  &--reopened {
    background: var(--color-warning-light);
  }

  &--deleted {
    background: var(--color-danger-light);
  }

  &--updated,
  &--priority_changed,
  &--description_updated {
    background: var(--color-warning-light);
  }

  &--note_created,
  &--note_updated {
    background: var(--color-info-light);
  }

  &--note_deleted {
    background: var(--color-danger-light);
  }

  &--activity_created {
    background: var(--color-success-light);
  }

  &--activity_updated {
    background: var(--color-info-light);
  }

  &--activity_deleted {
    background: var(--color-danger-light);
  }

  &--communication_sent {
    background: var(--color-success-light);
  }

  &--communication_failed {
    background: var(--color-danger-light);
  }

  &--communication_scheduled {
    background: var(--color-background-light);
  }

  &--assigned,
  &--unassigned {
    background: var(--color-info-light);
  }
}

.timeline-card {
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--border-radius-medium);
  padding: var(--space-normal);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  transition: box-shadow 0.2s ease;

  &:hover {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }

  &--communication {
    background: transparent;
    border: none;
    box-shadow: none;
    padding: 0;

    &:hover {
      box-shadow: none;
    }
  }
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: var(--space-small);
  gap: var(--space-normal);
}

.card-title {
  font-size: var(--font-size-medium);
  font-weight: var(--font-weight-bold);
  color: var(--color-body);
  flex: 1;
}

.card-user {
  display: flex;
  align-items: center;
  gap: var(--space-smaller);
  flex-shrink: 0;

  .user-thumbnail {
    flex-shrink: 0;
  }

  .user-name {
    font-size: var(--font-size-small);
    color: var(--color-body);
    font-weight: var(--font-weight-medium);
  }
}

.card-content {
  .event-details {
    font-size: var(--font-size-default);
    color: var(--color-body);
    line-height: 1.6;
  }

  .ticket-info {
    display: flex;
    flex-direction: column;
    gap: var(--space-micro);

    .ticket-id {
      font-size: var(--font-size-small);
      color: var(--color-body-light);
      font-weight: var(--font-weight-medium);
    }

    .ticket-name {
      font-size: var(--font-size-medium);
      color: var(--color-body);
      font-weight: var(--font-weight-medium);
    }
  }

  .field-change {
    margin-bottom: var(--space-smaller);
    line-height: 1.6;

    &:last-child {
      margin-bottom: 0;
    }

    strong {
      color: var(--color-body);
      font-weight: var(--font-weight-bold);
    }
  }

  .event-description {
    color: var(--color-body);
  }

  .status-badge {
    display: inline-block;
    padding: var(--space-micro) var(--space-small);
    border-radius: var(--border-radius-small);
    font-size: var(--font-size-small);
    font-weight: var(--font-weight-bold);

    &--won {
      background: var(--color-success-light);
      color: var(--color-success);
    }
  }

  .loss-reason {
    margin-top: var(--space-smaller);
    padding: var(--space-small);
    background: var(--color-background-light);
    border-radius: var(--border-radius-small);
    font-size: var(--font-size-small);
    line-height: 1.5;

    strong {
      display: block;
      margin-bottom: var(--space-micro);
    }
  }
}

.load-more-container {
  text-align: center;
  padding: var(--space-normal);
  border-top: 1px solid var(--color-border);
}

.load-more-button {
  display: inline-flex;
  align-items: center;
  gap: var(--space-small);
  padding: var(--space-small) var(--space-normal);
}

.communication-details {
  display: flex;
  flex-direction: column;
  gap: var(--space-small);
}

.comm-label {
  display: block;
  font-size: var(--font-size-small);
  font-weight: var(--font-weight-bold);
  color: var(--color-body-light);
  margin-bottom: var(--space-micro);
}

.comm-body {
  display: block;
  font-size: var(--font-size-default);
  color: var(--color-body);
  line-height: 1.5;
  background: var(--color-background-light);
  border-radius: var(--border-radius-small);
  padding: var(--space-small);
}

.comm-reply {
  border-left: 3px solid var(--color-woot);
  padding-left: var(--space-small);
}

.comm-card-wrapper {
  display: flex;
  flex-direction: column;
  gap: 0;
}

.comm-bubble {
  background: var(--color-background-light);
  border: 1px solid var(--color-border);
  border-radius: var(--border-radius-medium);
  padding: var(--space-normal);

  &--failed {
    background: var(--color-danger-light);
    border-color: var(--color-danger);
  }

  &--scheduled {
    background: var(--color-background-light);
    border-color: var(--color-border);
  }
}

.comm-bubble-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: var(--space-small);
  gap: var(--space-small);
}

.comm-bubble-meta {
  font-size: var(--font-size-small);
  color: var(--color-body-light);
}

.comm-id-link {
  font-size: var(--font-size-small);
  font-weight: var(--font-weight-medium);
  color: var(--color-woot);
  text-decoration: underline;
  white-space: nowrap;
  flex-shrink: 0;

  &:hover {
    opacity: 0.8;
  }
}

.comm-scheduled-badge {
  font-size: var(--font-size-small);
  color: var(--color-body-light);
  white-space: nowrap;
  flex-shrink: 0;
}

.comm-bubble-body {
  font-size: var(--font-size-default);
  color: var(--color-body);
  line-height: 1.6;
}

.comm-failed-badge {
  font-size: var(--font-size-small);
  color: var(--color-danger);
  margin-bottom: var(--space-small);
}

.comm-reply-section {
  padding: var(--space-normal) 0 0 0;
}

.comm-reply-from {
  display: flex;
  align-items: center;
  gap: var(--space-smaller);
  margin-bottom: var(--space-small);
}

.comm-reply-arrow {
  font-size: var(--font-size-medium);
  color: var(--color-body-light);
}

.comm-reply-sender {
  font-size: var(--font-size-small);
  font-weight: var(--font-weight-bold);
  color: var(--color-body);
}

.comm-reply-date {
  font-size: var(--font-size-small);
  color: var(--color-body-light);
}

.comm-reply-body {
  font-size: var(--font-size-default);
  color: var(--color-body);
  line-height: 1.6;
  padding-left: var(--space-normal);
  border-left: 2px solid var(--color-border);
}

.comm-no-reply {
  padding-top: var(--space-small);
  font-size: var(--font-size-small);
  color: var(--color-body-lighter);
  font-style: italic;
}

.comm-conv-link {
  font-size: var(--font-size-small);
  font-weight: var(--font-weight-medium);
  color: var(--color-woot);
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
}
</style>
